import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { debounce } from 'lodash';
import { useState } from 'react';

import { Button, SearchInput, Select } from '../../../../components/index';
import { RangePickerStylized } from '../../../../components/common/datepicker/styles';
import { useGetOmittedStopoveresQuery } from '../../../../services/omissionApi';
import { Stopover as StopoverType } from '../../../../types';
import {
  FilterOmissionReportType,
  OmissionTypeType,
} from '../../../../typesFinancial';
import {
  createDateString,
  createDateStringPtBr,
} from '../../../../utils/utils';
import { SectionHeader } from '../styles';
import { ModalExportOmissionXml } from './modalExportOmissionXml';
import { OmissionReportTable } from './omissionReportTable';
import { omissionTypeLabels } from './omissionReportTableColumns';

const DEFAULT_PAGE_SIZE = 15;

// Colunas exportadas no CSV, na mesma ordem exibida na tabela.
const csvColumns: Array<{ label: string; key: keyof StopoverType }> = [
  { label: 'Escala', key: 'code' },
  { label: 'Tipo', key: 'omission_type' },
  { label: 'Embarcação', key: 'vessel_name' },
  { label: 'IMO', key: 'vessel_imo' },
  { label: 'DUV', key: 'duv' },
  { label: 'Berço', key: 'docking_place_name' },
  { label: 'Agente de navegação', key: 'shipping_agency_name' },
  { label: 'Data da omissão', key: 'omission_date' },
  { label: 'Motivo', key: 'omission_motivation' },
];

export function OmissionSection() {
  const [filters, setFilters] = useState<FilterOmissionReportType>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  });

  const [showExportXmlModal, setShowExportXmlModal] = useState(false);

  const { data: omittedStopoveres, isLoading: isLoadingOmittedStopoveres } =
    useGetOmittedStopoveresQuery(filters);

  // Qualquer mudança de filtro volta para a primeira página.
  function updateFilters(newFilters: Partial<FilterOmissionReportType>) {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  }

  function onChangePage(page: number) {
    setFilters((prev) => ({ ...prev, page }));
  }

  function onChangePageSize(page_size: number) {
    setFilters((prev) => ({ ...prev, page_size, page: 1 }));
  }

  function onChangeDatePicker(dates: any) {
    if (!dates) {
      updateFilters({ initial_date: undefined, final_date: undefined });
      return;
    }
    updateFilters({
      initial_date: dates[0]
        ? createDateString(dates[0], true) || undefined
        : undefined,
      final_date: dates[1]
        ? createDateString(dates[1], true) || undefined
        : undefined,
    });
  }

  // "Todos" ('') lista omissões e evasões, já que toda evasão também é uma
  // omissão.
  function onSelectOmissionType(value: unknown) {
    updateFilters({ omission_type: (value as OmissionTypeType) || undefined });
  }

  function onSearch(value: string) {
    updateFilters({
      duv_or_vessel_name_or_imo_or_code: value !== '' ? value : undefined,
    });
  }

  function formatCsvValue(
    stopover: StopoverType,
    key: keyof StopoverType
  ): string {
    const value = stopover[key];

    if (value === null || value === undefined) return '';
    if (key === 'omission_date') {
      return createDateStringPtBr(value as string) || '';
    }
    if (key === 'omission_type') {
      return omissionTypeLabels[value as string] || String(value);
    }
    return String(value);
  }

  const convertToCSV = (stopoveres: StopoverType[]) => {
    const header = csvColumns.map((column) => column.label).join(';');
    const lines = stopoveres.map((stopover) =>
      csvColumns
        // O motivo pode conter ponto e vírgula, então cada campo é escapado.
        .map(
          (column) =>
            `"${formatCsvValue(stopover, column.key).replace(/"/g, '""')}"`
        )
        .join(';')
    );
    return [header, ...lines].join('\r\n');
  };

  const downloadCSV = () => {
    const csvData = new Blob([convertToCSV(omittedStopoveres?.results || [])], {
      type: 'text/csv;charset=utf-8;',
    });
    const csvURL = URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `relatorio-omissoes.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <SectionHeader style={{ flexDirection: 'row', gap: '15px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '30%',
          }}
        >
          <SearchInput
            size="large"
            allowClear
            placeholder="Pesquisar DUV/embarcação/IMO/escala"
            style={{ width: '400px' }}
            onChange={debounce((evt) => onSearch(evt.target.value), 500)}
          />
        </div>
        <div
          style={{
            display: 'flex',
            marginRight: 'auto',
            width: '60%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <span style={{ width: '140px' }}>Data da omissão:{'   '}</span>
            <RangePickerStylized
              allowClear
              onChange={onChangeDatePicker}
              minuteStep={1}
              showTime={false}
              format={['DD/MM/YYYY', 'DD/MM/YYYY']}
              style={{ width: '240px', background: 'var(--neutral_lightest)' }}
            />
          </div>
          <div>
            <Select
              placeholder="tipo"
              style={{ width: '150px' }}
              $selectorStyle={{
                background: 'var(--neutral_lightest) !important',
              }}
              options={[
                { value: '', label: 'Todos' },
                { value: 'EVASION', label: omissionTypeLabels.EVASION },
              ]}
              value={filters.omission_type || ''}
              onChange={onSelectOmissionType}
            />
          </div>
          <Space size={18}>
            <Button icon={<DownloadOutlined />} onClick={downloadCSV}>
              Download CSV
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => setShowExportXmlModal(true)}
            >
              Relatório XML
            </Button>
          </Space>
        </div>
      </SectionHeader>
      <ModalExportOmissionXml
        showModal={showExportXmlModal}
        setShowModal={setShowExportXmlModal}
        initialDate={filters.initial_date}
        finalDate={filters.final_date}
      />
      <OmissionReportTable
        dataSource={omittedStopoveres?.results}
        totalItems={omittedStopoveres?.count}
        isLoadingOmissionReportData={isLoadingOmittedStopoveres}
        onChangePagination={onChangePage}
        onShowSizeChange={onChangePageSize}
        queryPage={filters.page}
        pageSize={filters.page_size}
      />
    </>
  );
}
